import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { transformUrl } from 'unpic';
import { useLocation } from '../hooks/useSafeLocation';
import { Alternates } from '../types/gatsby';

/** @knipignore */
export const query = graphql`
  fragment OpenGraphImage on ContentfulAsset {
    url
  }

  fragment SystemPageSeoAttributes on ContentfulPageSystem {
    node_locale
    path
    paths
    title
    seoTitle
    seoDescription {
      seoDescription
    }
    seoRobots {
      seoRobots
    }
    openGraphImage {
      ...OpenGraphImage
    }
  }

  fragment PageContainerSeoAttributes on ContentfulPageContainer {
    node_locale
    path
    paths
    title
    seoTitle
    seoDescription {
      seoDescription
    }
    seoRobots {
      seoRobots
    }
    openGraphImage {
      ...OpenGraphImage
    }
  }
`;

export type PageSeoProps = {
  seoTitle?: string;
  seoDescription?: {
    seoDescription: string;
  };
  seoRobots?: {
    seoRobots: string;
  }
  openGraphImage?: {
    url: string
  };
};

type SeoProps = {
  locale: string;
  title?: string;
  description?: string;
  robots?: string;
  alternates?: Alternates[];
  image?: string;
  children?: React.ReactElement;
  pagination?: {
    currentPage?: number;
    totalPages?: number;
  };
};

/**
 * @description
 * Use this component to set <head> information.
 * Note: use PrivateHead.ts for client-side only pages or pages that are behind login.
 */
const Seo = ({
  locale, title, description, robots = 'index,follow', alternates, image, children, pagination,
}: SeoProps) => {
  const { site } = useStaticQuery(
    graphql`
      query SeoData {
        site {
          siteMetadata {
            configuration {
              channel {
                name
                siteUrl
              }
              social {
                twitter {
                  handle
                }
              }
            }
          }
        }
      }
    `,
  );

  const config = site.siteMetadata.configuration;
  const fullTitle = title ? `${title} | ${config.channel.name}` : config.channel.name;
  const location = useLocation();
  const fullUrl = `${config.channel.siteUrl}${location.pathname}`;

  const getPageUrl = (page: number): string => {
    if (page === 1) return fullUrl;

    const url = new URL(fullUrl);
    url.searchParams.set('page', page.toString());
    return url.toString();
  };

  const imageUrl = image ? transformUrl({
    url: image,
    width: 400,
    height: 300,
  })?.toString() : null;

  const getCanonicalUrl = () => {
    if (pagination?.currentPage && pagination.currentPage > 1) {
      return getPageUrl(pagination.currentPage);
    }
    return fullUrl;
  };

  return (
    <>
      <html lang={locale} />

      <title>{fullTitle}</title>
      <meta name="twitter:title" content={fullTitle} />
      <meta property="og:title" content={fullTitle} />

      <meta name="robots" content={robots} />

      {description && (
        <>
          <meta name="description" content={description} />
          <meta name="twitter:description" content={description} />
          <meta property="og:description" content={description} />
        </>
      )}

      {imageUrl && (
        <>
          <meta name="twitter:image" content={imageUrl} />
          <meta property="og:image" content={imageUrl} />
        </>
      )}

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content={`@${config.social.twitter.handle}`} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={config.channel.name} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:locale" content={locale.replace('-', '_')} />

      {/* Canonical tag: Includes page parameter only when not on page 1 */}
      <link key="canonical" rel="canonical" href={getCanonicalUrl()} />

      {/* Pagination prev/next links when pagination is enabled */}
      {pagination && pagination.currentPage && pagination.totalPages && (
        <>
          {pagination.currentPage > 1 && (
            <link
              rel="prev"
              href={pagination.currentPage === 2 ? fullUrl : getPageUrl(pagination.currentPage - 1)}
            />
          )}

          {pagination.currentPage < pagination.totalPages && (
            <link
              rel="next"
              href={getPageUrl(pagination.currentPage + 1)}
            />
          )}
        </>
      )}

      {Array.isArray(alternates) && alternates.filter((alternate) => alternate.href !== '').map((alternate) => (
        <link
          rel="alternate"
          key={alternate.href + alternate.hrefLang}
          href={alternate.href}
          hrefLang={alternate.hrefLang}
        />
      ))}

      {children}
    </>
  );
};

export default Seo;
